import { useCallback, useEffect, useMemo, useState, Suspense } from 'react';
import { Outlet } from '@tanstack/react-router';

import { useSidebarState } from '../../../shared/hooks/use-sidebar-state';
import { WorkspaceLayoutProvider, type WorkspaceLayoutMode } from '../hooks';
import { WorkspaceSidebar } from './sidebar';

import { PageLoader } from '@/shared/components/loader/page-loader';
import { MotionDiv } from '@/shared/components/motion-div';
import { SidebarInset, SidebarProvider } from '@/shared/components/shadcn/sidebar';

export const WorkspaceLayout = ({ workspaceId }: { workspaceId: string }) => {
  const { isCollapsed, setCollapsed } = useSidebarState();
  const [layoutMode, setLayoutMode] = useState<WorkspaceLayoutMode>('default');
  const [focusSidebarOpen, setFocusSidebarOpen] = useState(false);

  useEffect(() => {
    if (layoutMode === 'discovery-focus') {
      setFocusSidebarOpen(false);
    }
  }, [layoutMode]);

  const handleLayoutModeChange = useCallback((nextMode: WorkspaceLayoutMode) => {
    setLayoutMode((prevMode) => (prevMode === nextMode ? prevMode : nextMode));
  }, []);

  const handleSidebarOpenChange = useCallback(
    (open: boolean) => {
      if (layoutMode === 'discovery-focus') {
        setFocusSidebarOpen(open);
        return;
      }

      setCollapsed(!open);
    },
    [layoutMode, setCollapsed],
  );

  const isSidebarOpen = layoutMode === 'discovery-focus' ? focusSidebarOpen : !isCollapsed;

  const layoutValue = useMemo(
    () => ({
      mode: layoutMode,
      setMode: handleLayoutModeChange,
      isSidebarOpen,
      setSidebarOpen: handleSidebarOpenChange,
      showSidebar: () => handleSidebarOpenChange(true),
    }),
    [handleLayoutModeChange, handleSidebarOpenChange, isSidebarOpen, layoutMode],
  );

  return (
    <WorkspaceLayoutProvider value={layoutValue}>
      <MotionDiv>
        <Suspense fallback={<PageLoader className="h-screen" />}>
          <SidebarProvider open={isSidebarOpen} onOpenChange={handleSidebarOpenChange}>
            <WorkspaceSidebar workspaceId={workspaceId} />
            <SidebarInset className="relative flex h-screen flex-1 flex-col gap-2 overflow-auto">
              <Suspense fallback={<PageLoader />}>
                <Outlet />
              </Suspense>
            </SidebarInset>
          </SidebarProvider>
        </Suspense>
      </MotionDiv>
    </WorkspaceLayoutProvider>
  );
};
